/**
 * feat-259 — the bottom-right element of every item detail panel's footer.
 *
 * Design authority: `design/save-status.html`. It is the only feedback left in
 * the panel once Save is gone, so its failure mode matters more than its
 * appearance: **a status that flickers on every tab-through is worse than no
 * status at all.**
 *
 * The implementable form of that rule, quoted from §3①: the state is a function
 * of the last WRITE, not of focus. THIS COMPONENT MUST NEVER RECEIVE AN
 * `onBlur` OR `onFocus` SIGNAL. If it cannot see focus, it cannot flicker on
 * focus — and AT3's "no visual change on a no-op blur" then holds by
 * construction rather than by a guard someone can delete. Its whole input is
 * `useAutoSave`'s request lifecycle.
 *
 * One component, five panels, no per-panel variants — which is what makes AT7
 * and AT11 dischargeable by parametrising over the five types instead of
 * writing five near-identical tests.
 */

import React, { useEffect, useState } from 'react';
import type { AutoSaveStatus } from './useAutoSave';
import { AUTO_SAVE_COPY } from './autoSaveCopy';
import { useApprovalGate } from './approvalGate';

/** The rendering vocabulary. `settled` is not one of the engine's states — it is
 *  `saved`, later. See SAVE_SETTLE_DELAY_MS. */
export type SaveState = 'idle' | 'saving' | 'saved' | 'settled' | 'error';

/** `✓ Saved` becomes `✓ Saved 2:31 PM` after this, IN PLACE, and then stays
 *  until the next write (save-status.html §3③). It does not fade out: a
 *  transient indicator that empties its slot re-creates the silence this
 *  feature exists to remove, and an editor who looks up a second late cannot
 *  tell "saved a moment ago" from "never saved". */
export const SAVE_SETTLE_DELAY_MS = 2000;

/** Time-only, local, no seconds. Mirrors `formatContentTimestamp`'s shape in
 *  ContentTimestamps.tsx — the component this one sits opposite — rather than
 *  reusing it, because that one is deliberately date-first (feat-252 §7 says
 *  the two formats must not be unified).
 *
 *  Exported so a test can compute its expectation with the SAME options instead
 *  of hand-typing `2:31 PM`. Do not assert against a literal: `Intl` emits
 *  U+202F before AM/PM in current ICU where older builds emit U+0020, the two
 *  are byte-different and pixel-identical, and a hand-typed string pins the
 *  machine the test last ran on. Flatten whitespace in any assertion. */
const SAVE_TIME_FORMAT: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit' };

export function formatSaveTime(ms: number): string {
  return new Date(ms).toLocaleTimeString(undefined, SAVE_TIME_FORMAT);
}

/** The mockup's inline SVG, with both strokes bound to `currentColor` so the
 *  ring inherits whatever ink the status is rendering in and no raw hex enters
 *  the file (NOTES §10.7). Structurally the same idea as
 *  `ReadingDetailForm.tsx:48`'s Spinner, which is deliberately NOT imported: it
 *  is bound to `border-reading`, a per-item-type accent, and this element is
 *  shared by all five types. */
export const SaveSpinner: React.FC<{ size?: number }> = ({ size = 12 }) => (
  <svg
    className="animate-spin"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
    data-testid="save-spinner"
  >
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.2" strokeWidth="3" />
    <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
  </svg>
);

export interface SaveStatusProps {
  /** The save controller's lifecycle. NOT focus — see the header. */
  status: AutoSaveStatus;
  /** When the panel last had nothing outstanding, or null before the first
   *  write of the session. */
  savedAt: number | null;
  /**
   * feat-270 — override for the tenant's curriculum approval gate. Normally
   * UNSET: the value comes from `ApprovalGateContext`, whose default is off, so
   * every existing caller and every flag-off tenant renders exactly today's
   * footer (AT-29). The prop exists so a test can drive the line directly.
   */
  approvalRequired?: boolean;
}

export const SaveStatus: React.FC<SaveStatusProps> = ({ status, savedAt, approvalRequired }) => {
  const gate = useApprovalGate();
  const showNotLiveYet = approvalRequired ?? gate.approvalRequired;
  const [showTime, setShowTime] = useState(false);

  useEffect(() => {
    if (savedAt === null) return undefined;
    setShowTime(false);
    const t = setTimeout(() => setShowTime(true), SAVE_SETTLE_DELAY_MS);
    return () => clearTimeout(t);
  }, [savedAt]);

  const isError = status === 'error';
  // Four of the five renderings are the same ink at the same size. ONLY the
  // failure changes colour, and it is also the only one that gains weight —
  // that is the whole hierarchy. `✓ Saved` is deliberately NOT green:
  // `success.DEFAULT` is 2.927:1 on white and is not a text token, and colour in
  // this footer means "something needs you". Spending it on the outcome that
  // fires fifty times an hour leaves nothing for the one that doesn't.
  const tone = isError ? 'text-danger-text font-semibold' : 'text-inkMuted';

  const body = () => {
    if (isError) {
      return (
        <>
          <span aria-hidden="true">⚠</span> {AUTO_SAVE_COPY.notSaved}
        </>
      );
    }
    if (status === 'saving') {
      return (
        <>
          <SaveSpinner size={12} />
          {AUTO_SAVE_COPY.saving}
        </>
      );
    }
    if (savedAt !== null) {
      return (
        <>
          <span aria-hidden="true">✓</span>{' '}
          {showTime ? AUTO_SAVE_COPY.savedAtTime(formatSaveTime(savedAt)) : AUTO_SAVE_COPY.saved}
          {/* Same ink, same size, same line — see `savedNotLiveYet`. */}
          {showNotLiveYet && <> · {AUTO_SAVE_COPY.savedNotLiveYet}</>}
        </>
      );
    }
    return <>{AUTO_SAVE_COPY.idle}</>;
  };

  return (
    <span
      // `polite`, never `assertive`: a routine save must not interrupt what a
      // screen-reader user is typing. The ✓ / ⚠ glyphs are aria-hidden and the
      // WORDS carry the meaning — `Not saved` must read as a failure with the
      // colour stripped out. The timestamp is font-sans (inherited), never
      // font-display: Fredoka's "1" is 383/1000em against its "2" at 577, so
      // `Saved 1:05 PM` and `Saved 2:31 PM` render at visibly different widths
      // and the settled state jitters as the clock rolls. `tabular-nums` does
      // not help — it is inert app-wide, neither brand font ships `tnum`.
      className={`inline-flex items-center gap-1.5 text-xs ${tone}`}
      aria-live="polite"
      aria-atomic="true"
      data-testid="save-status"
    >
      {body()}
    </span>
  );
};

export default SaveStatus;
